import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import "./App.css";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Layout Components
import AnnouncementBar from "./components/layout/AnnouncementBar";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import StickyMobileCTA from "./components/layout/StickyMobileCTA";

// Section Components - Premium Healing Sanctuary
import HeroSection from "./components/sections/HeroSection";
import CredibilityStrip from "./components/sections/CredibilityStrip";
import MissionSection from "./components/sections/MissionSection";
import PathsSection from "./components/sections/PathsSection";
import CrystalHealingSection from "./components/sections/CrystalHealingSection";
import AboutSection from "./components/sections/AboutSection";
import VisionSection from "./components/sections/VisionSection";
import ProgramsSection from "./components/sections/ProgramsSection";
import TestimonialsSection from "./components/sections/TestimonialsSection";
import GallerySection from "./components/sections/GallerySection";
import ConnectSection from "./components/sections/ConnectSection";
import BookSection from "./components/sections/BookSection";
import ReviewSubmitSection from "./components/sections/ReviewSubmitSection";
import DigitalResourcesSection from "./components/sections/DigitalResourcesSection";
import DynamicSectionsBlock from "./components/sections/DynamicSectionsBlock";

// Page Components
import BookPage from "./components/pages/BookPage";
import DownloadPage from "./pages/DownloadPage";
import ChallengePage from "./pages/ChallengePage";
import ChallengesPage from "./pages/ChallengesPage";
import DynamicPageWrapper from "./pages/DynamicPage";

// Booking Component
import BookingModal from "./components/booking/BookingModal";

// Admin Components
import AdminDashboard from "./components/admin/AdminDashboard";

// Main Website Component
const MainWebsite = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [bookingSessionType, setBookingSessionType] = useState('');

  const openBooking = (sessionType = '') => {
    setBookingSessionType(sessionType);
    setIsBookingOpen(true);
  };

  return (
    <LanguageProvider>
      <div className="App min-h-screen bg-charcoal">
        <AnnouncementBar />
        <Header onRegisterClick={() => openBooking()} />
        <main>
          {/* 1. Hero */}
          <HeroSection onRegisterClick={() => openBooking()} />
          {/* 2. Credibility strip — name + credentials immediately below hero */}
          <CredibilityStrip />
          {/* 3. Mission */}
          <MissionSection />
          {/* 4. Programs — commercial offer early */}
          <ProgramsSection onRegisterClick={openBooking} />
          {/* 5. Testimonials — social proof directly after offer */}
          <TestimonialsSection />
          {/* 6. Digital Resources — data-driven; renders only when assets exist */}
          <DigitalResourcesSection />
          {/* 7. Crystal Healing — featured service */}
          <CrystalHealingSection onRegisterClick={() => openBooking('crystal-healing')} />
          {/* 7. About Mona */}
          <AboutSection />
          {/* 8. Vision / Philosophy */}
          <VisionSection />
          {/* 9–13. Remaining sections */}
          <PathsSection />
          <BookSection />
          <GallerySection />
          <DynamicSectionsBlock />
          <ConnectSection />
          <ReviewSubmitSection />
        </main>
        <Footer />
        <StickyMobileCTA onBookClick={() => openBooking()} />
        <Toaster position="top-center" />

        {/* Booking Modal */}
        <BookingModal
          isOpen={isBookingOpen}
          onClose={() => setIsBookingOpen(false)}
          sessionType={bookingSessionType}
          sourcePage={window.location.pathname}
        />
      </div>
    </LanguageProvider>
  );
};

// Book Page Wrapper
const BookPageWrapper = () => {
  return (
    <LanguageProvider>
      <BookPage />
      <Toaster position="top-center" />
    </LanguageProvider>
  );
};

// Challenge Page Wrapper
const ChallengePageWrapper = () => (
  <LanguageProvider>
    <ChallengePage />
  </LanguageProvider>
);

// Admin Page Component
const AdminPage = () => {
  return (
    <AuthProvider>
      <AdminDashboard />
      <Toaster position="top-center" />
    </AuthProvider>
  );
};

// Main App with Routing
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/*" element={<AdminPage />} />
        <Route path="/challenge/:slug" element={<ChallengePageWrapper />} />
        <Route path="/challenges" element={<LanguageProvider><ChallengesPage /></LanguageProvider>} />
        <Route path="/book/positivity" element={<BookPageWrapper />} />
        <Route path="/download/:shareToken" element={<DownloadPage />} />
        <Route path="/:slug" element={<DynamicPageWrapper />} />
        <Route path="/*" element={<MainWebsite />} />
      </Routes>
    </Router>
  );
}

export default App;
